import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createHash, timingSafeEqual } from "crypto";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const PIN_SCHEMA = z.string().regex(/^\d{4,8}$/, "PIN must be 4–8 digits");

function hashPin(pin: string, userId: string) {
  return createHash("sha256").update(`${userId}:${pin}`).digest("hex");
}

function safeEqHex(a: string, b: string) {
  if (a.length !== b.length) return false;
  try {
    return timingSafeEqual(Buffer.from(a, "hex"), Buffer.from(b, "hex"));
  } catch {
    return false;
  }
}

export const getParentLockStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("profiles")
      .select("parent_pin_hash, account_type")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return {
      hasPin: !!data?.parent_pin_hash,
      accountType: (data?.account_type ?? "adult") as "adult" | "student",
    };
  });

export const setAccountType = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ accountType: z.enum(["adult", "student"]) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("profiles")
      .update({ account_type: data.accountType })
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const setParentPin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        newPin: PIN_SCHEMA,
        currentPin: z.string().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: profile, error: readErr } = await supabase
      .from("profiles")
      .select("parent_pin_hash")
      .eq("user_id", userId)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);

    if (profile?.parent_pin_hash) {
      if (!data.currentPin) throw new Error("Current PIN required to change PIN.");
      if (!safeEqHex(profile.parent_pin_hash, hashPin(data.currentPin, userId))) {
        throw new Error("Current PIN is incorrect.");
      }
    }

    const { error } = await supabase
      .from("profiles")
      .update({ parent_pin_hash: hashPin(data.newPin, userId) })
      .eq("user_id", userId);
    if (error) throw new Error(error.message);

    return { ok: true };
  });

export const verifyParentPin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ pin: PIN_SCHEMA }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: profile, error } = await supabase
      .from("profiles")
      .select("parent_pin_hash")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!profile?.parent_pin_hash) return { ok: false, reason: "no_pin" as const };
    const ok = safeEqHex(profile.parent_pin_hash, hashPin(data.pin, userId));
    return { ok, reason: ok ? null : ("bad_pin" as const) };
  });

export const clearParentPin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ currentPin: PIN_SCHEMA }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: profile, error: readErr } = await supabase
      .from("profiles")
      .select("parent_pin_hash")
      .eq("user_id", userId)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);
    if (!profile?.parent_pin_hash) return { ok: true };
    if (!safeEqHex(profile.parent_pin_hash, hashPin(data.currentPin, userId))) {
      throw new Error("Current PIN is incorrect.");
    }
    const { error } = await supabase
      .from("profiles")
      .update({ parent_pin_hash: null })
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
